import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Spin,
  Alert,
  Typography,
} from "antd";
import {
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import FooterSection from "../../shared/components/footerSection";
import "./styles.css";
import "./dashboard.css";
import StatCards from "./components/StatCards";
import CompanyStatusChart from "./components/CompanyStatusChart";
import QuickAccessCards from "./components/QuickAccessCards";
import PaymentStatsCards from "./components/PaymentStatsCards";
import RevenueChart from "./components/RevenueChart";
import TopPayingCompanies from "./components/TopPayingCompanies";
import CompanyStatusTabs from "./components/CompanyStatusTabs";
import superAdminService from "../../../services/super-admin/super-admin.service";
import { SuperAdminDashboardData } from "../../../types/super-admin.types";

const { Title, Text } = Typography;

const SuperAdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<SuperAdminDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await superAdminService.getDashboardData('monthly');
        setDashboardData(data);
      } catch (err: any) {
        handleApiError(err, t);
        setError("Gösterge paneli verileri yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [t]);

  if (loading) {
    return (
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-center align-items-center">
        <Spin size="large" />
        <p className="mt-3">Veriler yükleniyor...</p>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-center align-items-center">
        <Alert
          message="Hata"
          description={error || "Gösterge paneli verileri alınamadı."}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const { kpiMetrics, revenueMetrics, charts, leaderboards, subscriptionBreakdown } = dashboardData;

  return (
    <>
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill">
          <div className="dashboard-header">
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb">
              <div className="my-auto mb-2">
                <Title level={3} className="mb-1 text-white">Süper Admin Paneli</Title>
                <Text type="secondary" className="text-white">
                  Hoş geldiniz! İşte sistemin genel durumu.
                </Text>
              </div>
            </div>
          </div>

          <div className="dashboard-container">
            {kpiMetrics && <StatCards stats={kpiMetrics} revenueMetrics={revenueMetrics} />}
            
            {/* {revenueMetrics && <PaymentStatsCards stats={revenueMetrics} loading={loading} />} */}
            
            <Row gutter={[16, 16]} className="mb-4 equal-height-row">
              {/* <Col xs={24} lg={12}>
                {subscriptionBreakdown && <CompanyStatusChart data={subscriptionBreakdown} />}
              </Col> */}
              <Col xs={24} lg={24}>
                {leaderboards?.topPayingCompanies ? (
                  <TopPayingCompanies 
                    companies={leaderboards.topPayingCompanies}
                    loading={loading}
                  />
                ) : (
                  <Card className="border-0 shadow-sm">
                    <div className="text-center p-4">
                      <ExclamationCircleOutlined className="text-warning mb-2" style={{ fontSize: '32px' }} />
                      <Title level={5} className="text-muted">Veri Bulunamadı</Title>
                      <Text type="secondary">En çok ödeme yapan şirket verisi mevcut değil.</Text>
                    </div>
                  </Card>
                )}
              </Col>
            </Row>
            
            {subscriptionBreakdown && (
              <CompanyStatusTabs
                activeCompanies={subscriptionBreakdown.active.companies}
                trialCompanies={subscriptionBreakdown.trial.companies}
                gracePeriodCompanies={subscriptionBreakdown.inGracePeriod.companies}
                expiredCompanies={subscriptionBreakdown.expired.companies}
                loading={loading}
              />
            )}

            <Row gutter={16} className="mb-4">
              <Col xs={24}>
                {charts?.last12MonthsRevenue ? (
                  <RevenueChart 
                    data={charts.last12MonthsRevenue}
                    loading={loading}
                  />
                ) : (
                   <Card className="border-0 shadow-sm">
                    <div className="text-center p-4">
                      <ExclamationCircleOutlined className="text-warning mb-2" style={{ fontSize: '32px' }} />
                      <Title level={5} className="text-muted">Gelir Grafiği Yüklenemedi</Title>
                      <Text type="secondary">Aylık gelir verisi alınamadı.</Text>
                    </div>
                  </Card>
                )}
              </Col>
            </Row>
            
            {kpiMetrics && <QuickAccessCards companyCount={kpiMetrics.totalCompanies} />}
            
          </div>
        </div>
        
        {/* <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div> */}
      </div>
    </>
  );
};

export default SuperAdminDashboard; 